import React, { FC, useEffect } from 'react';
import { BodyText, FlexCol, FlexRow, FlexStyleProps } from '@minecraft.atoms';
import { WindowWidthContext } from '@minecraft.utils';
import GridPaginationProvider from '@minecraft.gridpagination';
import Select, { Option } from '@minecraft.select';
import { useTranslation } from 'react-i18next';

/**
 * A wrapper for pagination that works with AG Grid and allows the use to modify page size on mobile. This provides all the necessary components and display for pagination
 * of data from the backend.
 *
 */

export interface GridPaginationProps {
  pageNumber: number;
  pageSize: number;
  setPageNumber: (pageNumber: number) => void;
  setPageSize: (pageSize: number) => void;
  totalRecords: number;
  hideTopResultText?: boolean;
  hideSwitchers?: boolean;
  loading?: boolean;
  flexGrow?: FlexStyleProps['grow'];
  ActionSlotComponent?: FC | React.ReactNode;
  pageSizeOnMobile?: boolean;
  sortOptionsOnMobile?: boolean;
  sortOptions?: Option[];
  sortValue?: string;
  handleSortEvent: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const GridPagination: React.FC<GridPaginationProps> = ({
  children,
  pageNumber,
  pageSize,
  setPageNumber,
  setPageSize,
  totalRecords,
  hideSwitchers = false,
  hideTopResultText = false,
  loading,
  flexGrow = 1,
  ActionSlotComponent,
  pageSizeOnMobile = false,
  sortOptionsOnMobile = false,
  sortOptions = [],
  sortValue = 0,
  handleSortEvent,
}) => {
  const isMobile = WindowWidthContext.useIsMobile();

  const { t } = useTranslation();

  // If the page number * the page size > total records, then we set the current page number to the last page
  // This is to prevent the user from being on a page that has no data
  useEffect(() => {
    if (totalRecords && pageNumber * pageSize - pageSize > totalRecords) {
      setPageNumber(Math.ceil(totalRecords / pageSize));
    }
  }, [pageNumber, pageSize, totalRecords, setPageNumber]);

  return (
    <GridPaginationProvider
      totalRecords={totalRecords}
      defaultResultSizeOptions={[25, 50, 100]}
      onPageChanged={(newPage) => {
        if (newPage.pageLimit !== pageSize && !Number.isNaN(newPage.pageLimit)) {
          setPageSize(Number(newPage.pageLimit));
          setPageNumber(1);
          window.scrollTo({ top: 0 });
        }
        if (newPage.currentPage !== pageNumber && !Number.isNaN(newPage.currentPage)) {
          setPageNumber(Number(newPage.currentPage));
          window.scrollTo({ top: 0 });
        }
      }}
      pageLimit={pageSize}
      currentPage={pageNumber}
    >
      {!hideTopResultText && (
        <FlexRow
          justifyContent="space-between"
          alignItems="center"
          className="cn_w-full"
          data-testid="grid-pagination-upper-results"
          grow={flexGrow}
          gap="2"
        >
          <GridPaginationProvider.ResultText showResponsiveSelect={false} />
          {ActionSlotComponent}
          {!hideSwitchers && <GridPaginationProvider.Switcher /> && (
            <FlexRow justifyContent={isMobile ? 'stretch' : 'flex-end'} data-testid="grid-pagination-upper-switcher">
              <GridPaginationProvider.Switcher pageSizeOnMobile={pageSizeOnMobile} loading={loading} />
            </FlexRow>
          )}
        </FlexRow>
      )}
      {sortOptionsOnMobile && isMobile && (
        <FlexCol className="cn_w-full" data-testid="grid-pagination-sort-options">
          <BodyText fontWeight="semibold">{t('common:form.label.sortBy')}</BodyText>
          <Select
            className="cn_mt-1 cn_w-full"
            grow={1}
            value={sortValue}
            options={sortOptions}
            onChange={handleSortEvent}
            useFullWidth
          />
        </FlexCol>
      )}
      {children}
    </GridPaginationProvider>
  );
};
